
import React, { useEffect, useState } from "react";
import { ArrowLeft, UserCog, AlertTriangle } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PartnerForm from "@/components/forms/PartnerForm";
import { useToast } from "@/components/ui/use-toast";
import { usePartners } from "@/context/PartnerContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";

const EditPartner: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { getPartnerById } = usePartners();
  const [partner, setPartner] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setError("Δεν έχει οριστεί αναγνωριστικό συνεργάτη");
      setLoading(false);
      return;
    }

    try {
      const partnerData = getPartnerById(id);
      if (!partnerData) {
        setError("Ο συνεργάτης δεν βρέθηκε");
      } else {
        setPartner(partnerData);
      }
    } catch (error) {
      console.error("Error fetching partner:", error);
      setError("Σφάλμα κατά την ανάκτηση του συνεργάτη");
    } finally {
      setLoading(false);
    }
  }, [id, getPartnerById]);

  const handleSuccess = () => {
    toast({
      title: "Επιτυχής ενημέρωση!",
      description: "Ο συνεργάτης ενημερώθηκε επιτυχώς.",
    });
    navigate("/admin/partners");
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" asChild className="rounded-full h-10 w-10 border-slate-200">
            <Link to="/admin/partners">
              <ArrowLeft className="h-5 w-5 text-slate-600" />
            </Link>
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md">
              <UserCog className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-900">
                {loading ? "Φόρτωση..." : partner ? `Επεξεργασία Συνεργάτη` : "Σφάλμα"}
              </h1>
              <p className="text-sm text-slate-500">
                {loading ? "Παρακαλώ περιμένετε..." : partner ? partner.name : "Μη διαθέσιμο"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <Card>
          <CardContent className="p-6 space-y-6">
            <div className="flex gap-4">
              <Skeleton className="h-20 w-20 rounded-full" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-6 w-1/3" />
                <Skeleton className="h-4 w-1/4" />
              </div>
            </div>
            <div className="space-y-4">
              <Skeleton className="h-12 w-full" />
              <div className="grid grid-cols-2 gap-4">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
              <Skeleton className="h-32 w-full" />
            </div>
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-8 text-center flex flex-col items-center justify-center">
            <div className="p-3 bg-red-100 rounded-full mb-4">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-red-800 mb-2">Σφάλμα</h2>
            <p className="text-red-600 mb-6 max-w-md">{error}</p>
            <Button asChild className="btn-gradient bg-gradient-to-r from-red-600 to-pink-600">
              <Link to="/admin/partners">Επιστροφή στους Συνεργάτες</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <PartnerForm onSuccess={handleSuccess} initialData={partner} isAdminView={true} />
      )}
    </div>
  );
};

export default EditPartner;
